import * as React from 'react'
import { PropsWithChildren, ReactElement, useState } from 'react'
import {
    bundleIcon,
    DismissFilled,
    DismissRegular,
    MailArrowForwardFilled,
    MailArrowForwardRegular,
    MailCopyFilled,
    MailCopyRegular,
} from '@fluentui/react-icons'
import { Formik } from 'formik'
import useProcessEmail from '../../../hooks/email/useProcessEmail'
import useUser from '../../../hooks/useUser'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import FormContent from './FormContent'
import * as Yup from 'yup'
import { ProcessEmailDialogProps } from './ProcessEmailDialog'
import { useTranslation } from 'react-i18next'

export type ProcessEmailFormProps = PropsWithChildren<
    ProcessEmailDialogProps & {
        onDismiss: () => void
        setSubmitForm?: (submit: () => Promise<any>) => void
        setIsSubmitting?: (isSubmitting: boolean) => void
        globalErrors?: NormalizedError[]
    }
>

export enum EmailProcessingAction {
    Copy = 'COPY',
    Move = 'MOVE',
    File = 'FILE',
    Ignore = 'IGNORE',
}

export const EmailProcessingActionLabelMap = new Map([
    [EmailProcessingAction.Copy, 'action.copy'],
    [EmailProcessingAction.Move, 'action.move'],
    [EmailProcessingAction.File, 'action.file'],
    [EmailProcessingAction.Ignore, 'action.not_file'],
])

export const EmailProcessingActionIconMap = new Map([
    [EmailProcessingAction.Copy, bundleIcon(MailCopyFilled, MailCopyRegular)],
    [EmailProcessingAction.Move, bundleIcon(MailArrowForwardFilled, MailArrowForwardRegular)],
    [EmailProcessingAction.File, bundleIcon(MailArrowForwardFilled, MailArrowForwardRegular)],
    [EmailProcessingAction.Ignore, bundleIcon(DismissFilled, DismissRegular)],
])

export type ProcessEmailValues = {
    references: string[]
    userEmail: string
    notificationEmail: string
}

// Construct new set of references for the selected email(s).
function handleReferences(action: EmailProcessingAction, oldReference: string, originalReferences: string[], newReferences: string[]) {
    if (action === EmailProcessingAction.Move) {
        return [...new Set(originalReferences.filter(ref => ref !== oldReference).concat(newReferences))]
    } else if (action === EmailProcessingAction.Copy) {
        return [...new Set(originalReferences.concat(newReferences))]
    } else if (action === EmailProcessingAction.File) {
        return [...new Set(newReferences)]
    } else if (action === EmailProcessingAction.Ignore) {
        return []
    } else {
        return originalReferences
    }
}

const getValidationSchema = (translate: (key: string) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        references: Yup.array().test({
            message: translate('email.filing.validation'),
            test: arr => arr.length > 0,
        }),
    })
}

export default function ProcessEmailForm(props: ProcessEmailFormProps): ReactElement {
    const [globalErrors, setGlobalErrors] = useState<NormalizedError[]>()
    const { processSelection } = useProcessEmail()
    const { currentEmail } = useUser()
    const { t: translate } = useTranslation()

    return (
        <Formik<ProcessEmailValues>
            initialValues={{
                references: [],
                userEmail: props.emailMailbox,
                notificationEmail: currentEmail,
            }}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={props.action !== EmailProcessingAction.Ignore && getValidationSchema(translate)}
            onSubmit={(values, formikHelpers) => {
                processSelection(
                    values.userEmail,
                    values.notificationEmail,
                    props.selection,
                    references => handleReferences(props.action, props.file?.data?.reference, references, values.references),
                    () => {
                        setGlobalErrors(undefined)
                    },
                    () => {
                        if (!globalErrors) {
                            props.onDismiss()
                            props.reset()
                            formikHelpers.resetForm()
                        }
                    },
                ).catch(error => setGlobalErrors([normalizeAxiosError(error)]))
            }}
        >
            <FormContent {...props} />
        </Formik>
    )
}
